import React, { useState, useEffect } from 'react';
import { 
  Dialog, DialogTitle, DialogContent, DialogActions, 
  Button, Select, MenuItem, FormControl, InputLabel, Alert, Box, 
  Typography, Table, TableBody, TableCell, TableContainer, TableHead, 
  TableRow, Paper, IconButton, Chip, Divider, TextField
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutlineOutlined';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import WarningIcon from '@mui/icons-material/Warning';
import supabase from '../../../supabaseClient';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import dayjs from 'dayjs';
import { buildDeviceSessions } from '../../../Utils/machineProductionStats';

function AttendanceEditModal({
  open, 
  onClose, 
  attendance, 
  displayStart,
  displayEnd,
  checkInDateValue,
  checkOutDateValue,
  onRefresh, 
  idConfig: propIdConfig
}) {
  // Stany dla rozbitych 4 pól obecności głównej
  const [checkInTime, setCheckInTime] = useState(displayStart || '');
  const [checkOutTime, setCheckOutTime] = useState(attendance?.check_out ? displayEnd : '');
  const [checkInDate, setCheckInDate] = useState(checkInDateValue ? dayjs(checkInDateValue) : dayjs());
  const [checkOutDate, setCheckOutDate] = useState(attendance?.check_out ? dayjs(checkOutDateValue) : null);

  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  // Konfiguracja konta
  const [currentIdConfig, setCurrentIdConfig] = useState(propIdConfig || null);

  // Stan prac na maszynach
  const [deviceLogs, setDeviceLogs] = useState([]);
  const [devices, setDevices] = useState([]);
  const [availableStands, setAvailableStands] = useState([]);
  const [loadingStands, setLoadingStands] = useState(false);

  // Formularz dodawania / edycji sesji maszynowej
  const [editingSessionId, setEditingSessionId] = useState(null);
  const [newDeviceId, setNewDeviceId] = useState('');
  const [newStandId, setNewStandId] = useState('');
  const [newStartTime, setNewStartTime] = useState(null);
  const [newEndTime, setNewEndTime] = useState(null);

  useEffect(() => {
    if (!open) return;

    const resolveConfig = async () => {
      let cfg = propIdConfig || attendance?.id_configuration;
      if (!cfg) {
        const { data: { session } } = await supabase.auth.getSession();
        if (session) {
          const { data } = await supabase.from('profiles').select('id_configuration').eq('id', session.user.id).single();
          if (data) cfg = data.id_configuration;
        }
      }
      setCurrentIdConfig(cfg);
      if (cfg) {
        fetchDevices(cfg);
      }
    };

    setCheckInTime(displayStart || (attendance?.check_in ? dayjs(attendance.check_in).format('HH:mm') : ''));
    setCheckOutTime(attendance?.check_out ? (displayEnd || dayjs(attendance.check_out).format('HH:mm')) : '');
    setCheckInDate(attendance?.check_in ? dayjs(attendance.check_in) : (checkInDateValue ? dayjs(checkInDateValue) : dayjs()));
    setCheckOutDate(attendance?.check_out ? dayjs(attendance.check_out) : (checkOutDateValue ? dayjs(checkOutDateValue) : null));
    
    setErrorMsg('');
    resetMachineForm();
    resolveConfig();

    if (attendance?.id) {
      fetchDeviceLogs(attendance.id);
    }
  }, [open, attendance, displayStart, displayEnd, checkInDateValue, checkOutDateValue, propIdConfig]);

  // Pobieranie dedykowanych stanowisk pracownika dla wybranej maszyny (z kluczem device_stand_id)
  useEffect(() => {
    const fetchStandsForEmployeeAndDevice = async () => {
      if (!newDeviceId || !attendance?.employee_id) {
        setAvailableStands([]);
        return;
      }

      setLoadingStands(true);
      
      const { data, error } = await supabase
        .from('employee_device_stands')
        .select('device_stand_id, device_stands!inner(id, name, device_id)')
        .eq('employee_id', attendance.employee_id)
        .eq('device_stands.device_id', Number(newDeviceId));

      setLoadingStands(false);

      if (error) {
        console.error("Błąd podczas pobierania stanowisk pracownika:", error);
        setAvailableStands([]);
        return;
      }

      if (data) {
        const mappedStands = data
          .map(item => item.device_stands)
          .filter(Boolean);
        setAvailableStands(mappedStands);
      }
    };

    fetchStandsForEmployeeAndDevice();
  }, [newDeviceId, attendance?.employee_id]);

  const fetchDevices = async (configId) => {
    const { data: devData } = await supabase
      .from('devices')
      .select('id, name')
      .eq('id_configuration', configId);

    if (devData) setDevices(devData);
  };

  const fetchDeviceLogs = async (attendanceId) => {
    const { data, error } = await supabase
      .from('device_attendance')
      .select('*, devices(id, name), device_stands(id, name)')
      .eq('attendance_id', attendanceId)
      .order('timestamp', { ascending: true });

    if (!error && data) {
      setDeviceLogs(data);
    }
  };

  const sessions = buildDeviceSessions(deviceLogs);
  const hasUnclosedMachineWork = sessions.some(s => s.isOpen);

  const resetMachineForm = () => {
    setEditingSessionId(null);
    setNewDeviceId('');
    setNewStandId('');
    setNewStartTime(null);
    setNewEndTime(null);
  };

  const handleEditSessionInit = (session) => {
    setEditingSessionId(session.id);
    setNewDeviceId(session.deviceId || '');
    setNewStandId(session.standId || '');
    setNewStartTime(session.startTime ? dayjs(session.startTime) : null);
    setNewEndTime(session.endTime ? dayjs(session.endTime) : null);
  };

  const getFullCheckIn = () => {
    if (!checkInDate || !checkInTime) return null;
    return dayjs(`${checkInDate.format('YYYY-MM-DD')}T${checkInTime}`);
  };

  const getFullCheckOut = () => {
    if (!checkOutDate || !checkOutTime) return null;
    return dayjs(`${checkOutDate.format('YYYY-MM-DD')}T${checkOutTime}`);
  };

  const handleSave = async () => {
    setErrorMsg('');

    const mainStart = getFullCheckIn();
    const mainEnd = getFullCheckOut();

    if (!mainStart || !mainStart.isValid()) {
      setErrorMsg('Podaj poprawną datę oraz godzinę wejścia głównego.');
      return;
    }

    if (checkOutTime && checkOutDate) {
      if (!mainEnd || !mainEnd.isValid()) {
        setErrorMsg('Podaj poprawną datę oraz godzinę wyjścia.');
        return;
      }

      if (mainEnd.isBefore(mainStart) || mainEnd.isSame(mainStart)) {
        setErrorMsg('Data i godzina wyjścia muszą być późniejsze niż czas wejścia!');
        return;
      }

      if (hasUnclosedMachineWork) {
        setErrorMsg('Nie można ustawić wyjścia głównego! Pracownik ma otwartą sesję na maszynie.');
        return;
      }
    }

    for (const s of sessions) {
      const sStart = dayjs(s.startTime);
      const sEnd = s.endTime ? dayjs(s.endTime) : null;

      if (sStart.isBefore(mainStart)) {
        setErrorMsg(`Start na maszynie (${sStart.format('DD.MM HH:mm')}) jest wcześniejszy niż wejście główne!`);
        return;
      }
      if (mainEnd && sEnd && sEnd.isAfter(mainEnd)) {
        setErrorMsg(`Koniec pracy na maszynie (${sEnd.format('DD.MM HH:mm')}) wykracza poza wyjście główne!`);
        return;
      }
    }

    const { error } = await supabase
      .from('attendance')
      .update({
        check_in: mainStart.toISOString(),
        check_out: mainEnd ? mainEnd.toISOString() : null
      })
      .eq('id', attendance.id);

    if (error) {
      console.error(error);
      setErrorMsg('Błąd podczas zapisywania obecności.');
    } else {
      onRefresh();
      onClose();
    }
  };

  const handleSaveMachineWork = async () => {
    setErrorMsg('');

    // Precyzyjne ustalenie głównego startu i końca obecności
    let mainStart = getFullCheckIn();
    if (!mainStart || !mainStart.isValid()) {
      if (attendance?.check_in) {
        mainStart = dayjs(attendance.check_in);
      }
    }

    let mainEnd = getFullCheckOut();
    if (!mainEnd || !mainEnd.isValid()) {
      if (attendance?.check_out) {
        mainEnd = dayjs(attendance.check_out);
      }
    }

    if (!newDeviceId) {
      setErrorMsg('Musisz wybrać maszynę.');
      return;
    }

    if (!newStandId) {
      setErrorMsg('Musisz wybrać stanowisko dla wybranej maszyny.');
      return;
    }

    if (!newStartTime || !newStartTime.isValid()) {
      setErrorMsg('Podaj poprawny czas rozpoczęcia pracy na maszynie.');
      return;
    }

    if (!mainStart || !mainStart.isValid()) {
      setErrorMsg('Najpierw określ poprawny czas wejścia głównego pracownika!');
      return;
    }

    // Walidacja: Rozpoczęcie pracy na maszynie nie może być wcześniejsze niż wejście główne
    if (newStartTime.isBefore(mainStart)) {
      setErrorMsg(
        `Rozpoczęcie pracy na maszynie (${newStartTime.format('DD.MM.YYYY HH:mm')}) nie może być wcześniejsze niż wejście główne (${mainStart.format('DD.MM.YYYY HH:mm')})!`
      );
      return;
    }

    // Walidacje dla zakończonej obecności głównej
    if (mainEnd && mainEnd.isValid()) {
      if (!newEndTime || !newEndTime.isValid()) {
        setErrorMsg(
          'Ta obecność główna jest zakończona! Każda praca na maszynie musi posiadać zdefiniowaną datę i godzinę zakończenia.'
        );
        return;
      }

      if (newStartTime.isAfter(mainEnd)) {
        setErrorMsg(
          `Rozpoczęcie pracy na maszynie (${newStartTime.format('DD.MM.YYYY HH:mm')}) wykracza poza wyjście główne (${mainEnd.format('DD.MM.YYYY HH:mm')})!`
        );
        return;
      }

      if (newEndTime.isAfter(mainEnd)) {
        setErrorMsg(
          `Czas zakończenia pracy na maszynie (${newEndTime.format('DD.MM.YYYY HH:mm')}) wykracza poza wyjście główne (${mainEnd.format('DD.MM.YYYY HH:mm')})!`
        );
        return;
      }
    }

    // Walidacja czasu końca vs startu pracy na maszynie
    if (newEndTime && newEndTime.isValid()) {
      if (newEndTime.isBefore(newStartTime) || newEndTime.isSame(newStartTime)) {
        setErrorMsg('Czas zakończenia pracy na maszynie musi być późniejszy niż rozpoczęcia!');
        return;
      }
    }

    const startIso = newStartTime.toISOString();
    const endIso = newEndTime && newEndTime.isValid() ? newEndTime.toISOString() : null;

    if (editingSessionId) {
      const sessionToEdit = sessions.find(s => s.id === editingSessionId);
      if (!sessionToEdit) return;

      const { error: errStart } = await supabase
        .from('device_attendance')
        .update({
          device_id: Number(newDeviceId),
          stand_id: Number(newStandId),
          timestamp: startIso
        })
        .eq('id', sessionToEdit.startId);

      if (errStart) {
        setErrorMsg('Błąd aktualizacji startu sesji na maszynie.');
        return;
      }

      if (sessionToEdit.stopId) {
        if (endIso) {
          await supabase
            .from('device_attendance')
            .update({
              device_id: Number(newDeviceId),
              stand_id: Number(newStandId),
              timestamp: endIso
            })
            .eq('id', sessionToEdit.stopId);
        } else {
          await supabase.from('device_attendance').delete().eq('id', sessionToEdit.stopId);
        }
      } else if (endIso) {
        await supabase.from('device_attendance').insert([{
          employee_id: attendance.employee_id,
          device_id: Number(newDeviceId),
          stand_id: Number(newStandId),
          attendance_id: attendance.id,
          type: 'STOP_WORK',
          timestamp: endIso,
          id_configuration: currentIdConfig || attendance.id_configuration
        }]);
      }

    } else {
      const { data: startRecord, error: err1 } = await supabase.from('device_attendance').insert([{
        employee_id: attendance.employee_id,
        device_id: Number(newDeviceId),
        stand_id: Number(newStandId),
        attendance_id: attendance.id,
        type: 'START_WORK',
        timestamp: startIso,
        id_configuration: currentIdConfig || attendance.id_configuration
      }]).select().single();

      if (err1) {
        setErrorMsg('Błąd dodawania startu na maszynie.');
        return;
      }

      if (endIso && startRecord) {
        await supabase.from('device_attendance').insert([{
          employee_id: attendance.employee_id,
          device_id: Number(newDeviceId),
          stand_id: Number(newStandId),
          attendance_id: attendance.id,
          type: 'STOP_WORK',
          timestamp: endIso,
          id_configuration: currentIdConfig || attendance.id_configuration
        }]);
      }
    }

    resetMachineForm();
    fetchDeviceLogs(attendance.id);
  };

  const handleDeleteSession = async (session) => {
    const idsToDelete = [session.startId];
    if (session.stopId) idsToDelete.push(session.stopId);

    await supabase.from('device_attendance').delete().in('id', idsToDelete);
    if (editingSessionId === session.id) {
      resetMachineForm();
    }
    fetchDeviceLogs(attendance.id);
  };

  const endShift = async () => {
    setErrorMsg('');
    
    if (hasUnclosedMachineWork) {
      setErrorMsg('Nie można zakończyć zmiany! Zamknij najpierw otwartą pracę na maszynie.');
      return;
    }

    const now = dayjs();

    for (const s of sessions) {
      const sStart = dayjs(s.startTime);
      const sEnd = s.endTime ? dayjs(s.endTime) : null;

      if (sStart.isAfter(now)) {
        setErrorMsg(
          `Nie można zakończyć zmiany o ${now.format('HH:mm')}! Praca na maszynie (${s.deviceName}) zaczyna się później (${sStart.format('DD.MM.YYYY HH:mm')}). Dostosuj najpierw godziny prac na maszynach.`
        );
        return;
      }

      if (sEnd && sEnd.isAfter(now)) {
        setErrorMsg(
          `Nie można zakończyć zmiany o ${now.format('HH:mm')}! Zakończenie pracy na maszynie (${s.deviceName}) jest ustalone na później (${sEnd.format('DD.MM.YYYY HH:mm')}). Dostosuj najpierw godziny prac na maszynach.`
        );
        return;
      }
    }

    setCheckOutDate(now);
    setCheckOutTime(now.format('HH:mm'));

    const { error } = await supabase
      .from('attendance')
      .update({ check_out: now.toISOString() })
      .eq('id', attendance.id);

    if (error) {
      console.error(error);
      setErrorMsg('Błąd przy kończeniu zmiany.');
    } else {
      onRefresh();
      onClose();
    }
  };

  const resumeShift = async () => {
    setErrorMsg('');
    setCheckOutDate(null);
    setCheckOutTime('');

    const { error } = await supabase
      .from('attendance')
      .update({ check_out: null })
      .eq('id', attendance.id);

    if (error) {
      console.error(error);
      setErrorMsg('Nie udało się wznowić zmiany.');
    } else {
      onRefresh();
      onClose();
    }
  };

  const handleDeleteConfirm = async () => {
    if (sessions.length > 0) {
      setDeleteConfirmOpen(false);
      setErrorMsg('Nie można usunąć tego wpisu obecności! Najpierw usuń wszystkie zarejestrowane prace na maszynach z listy poniżej.');
      return;
    }

    const { error } = await supabase.from('attendance').delete().eq('id', attendance.id);
    if (error) {
      alert('Nie udało się usunąć wpisu');
    } else {
      onRefresh();
      onClose();
    }
    setDeleteConfirmOpen(false);
  };

  return (
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>
          Edytuj Wpis Obecności
        </DialogTitle>

        <DialogContent dividers>
          {errorMsg && (
            <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
              {errorMsg}
            </Alert>
          )}

          {hasUnclosedMachineWork && (
            <Alert severity="warning" icon={<WarningIcon />} sx={{ mb: 2, borderRadius: 2 }}>
              Pracownik ma niezakończoną pracę na maszynie! Wyjście główne jest zablokowane do momentu podania daty i godziny zakończenia pracy na stanowisku.
            </Alert>
          )}

          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, color: '#555' }}>
            Obecność główna
          </Typography>

          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 2 }}>
            <TimePicker
              label="Godzina wejścia"
              ampm={false}
              value={checkInTime ? dayjs(checkInTime, 'HH:mm') : null}
              onChange={value => setCheckInTime(value && value.isValid() ? value.format('HH:mm') : '')}
            />

            <DatePicker
              label="Dzień wejścia"
              format="DD-MM-YYYY"
              value={checkInDate}
              onChange={value => setCheckInDate(value && value.isValid() ? value : dayjs())}
            />

            <TimePicker
              label="Godzina wyjścia"
              ampm={false}
              disabled={hasUnclosedMachineWork}
              value={checkOutTime ? dayjs(checkOutTime, 'HH:mm') : null}
              onChange={value => setCheckOutTime(value && value.isValid() ? value.format('HH:mm') : '')}
            />

            <DatePicker
              label="Data wyjścia"
              format="DD-MM-YYYY"
              disabled={hasUnclosedMachineWork}
              value={checkOutDate}
              onChange={value => setCheckOutDate(value && value.isValid() ? value : null)}
            />
          </Box>

          <Divider sx={{ my: 3 }} />

          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: '#555' }}>
            Zarejestrowane prace na maszynach
          </Typography>

          <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2, mb: 3 }}>
            <Table size="small">
              <TableHead sx={{ bgcolor: '#fafafa' }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Maszyna</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Stanowisko</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Start</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Koniec</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>Akcja</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sessions.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ color: '#888', py: 2 }}>
                      Brak prac zarejestrowanych na maszynach.
                    </TableCell>
                  </TableRow>
                ) : (
                  sessions.map((s) => (
                    <TableRow key={s.id} selected={editingSessionId === s.id}>
                      <TableCell sx={{ fontWeight: 600 }}>{s.deviceName}</TableCell>
                      <TableCell>{s.standName}</TableCell>
                      <TableCell>{dayjs(s.startTime).format('DD.MM.YYYY HH:mm')}</TableCell>
                      <TableCell>{s.endTime ? dayjs(s.endTime).format('DD.MM.YYYY HH:mm') : '--:--'}</TableCell>
                      <TableCell>
                        {s.isOpen ? (
                          <Chip label="W trakcie" color="warning" size="small" sx={{ fontWeight: 600 }} />
                        ) : (
                          <Chip label="Zakończona" color="success" size="small" variant="outlined" />
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <IconButton size="small" color="primary" onClick={() => handleEditSessionInit(s)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" color="error" onClick={() => handleDeleteSession(s)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <Box sx={{ bgcolor: '#f9f9f9', p: 2, borderRadius: 2, border: '1px solid #e0e0e0', mb: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, color: '#333' }}>
              {editingSessionId ? 'Edytuj sesję na maszynie' : '+ Dodaj nową sesję na maszynie'}
            </Typography>

            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 2 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Maszyna</InputLabel>
                <Select
                  value={newDeviceId}
                  label="Maszyna"
                  onChange={(e) => { 
                    setNewDeviceId(e.target.value); 
                    setNewStandId(''); 
                  }}
                >
                  {devices.map((d) => <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>)}
                </Select>
              </FormControl>

              <FormControl fullWidth size="small" disabled={!newDeviceId || loadingStands}>
                <InputLabel>
                  {loadingStands ? 'Ładowanie stanowisk...' : 'Stanowisko'}
                </InputLabel>
                <Select
                  value={newStandId}
                  label="Stanowisko"
                  onChange={(e) => setNewStandId(e.target.value)}
                >
                  {availableStands.map((st) => (
                    <MenuItem key={st.id} value={st.id}>{st.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              <DateTimePicker
                label="Rozpoczęcie pracy"
                ampm={false}
                format="DD-MM-YYYY HH:mm"
                value={newStartTime}
                onChange={(val) => setNewStartTime(val)}
                slotProps={{ textField: { size: 'small' } }}
              />

              <DateTimePicker
                label={getFullCheckOut() ? "Zakończenie pracy (wymagane)" : "Zakończenie pracy (opcjonalnie)"}
                ampm={false}
                format="DD-MM-YYYY HH:mm"
                value={newEndTime}
                onChange={(val) => setNewEndTime(val)}
                slotProps={{ textField: { size: 'small' } }}
              />
            </Box>

            {newDeviceId && !loadingStands && availableStands.length === 0 && (
              <Alert severity="warning" sx={{ mb: 2, py: 0 }}>
                Ten pracownik nie ma przypisanych uprawnień do żadnego stanowiska na wybranej maszynie.
              </Alert>
            )}

            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="contained"
                startIcon={editingSessionId ? <SaveIcon /> : <AddCircleOutlineIcon />}
                onClick={handleSaveMachineWork}
                disabled={!newDeviceId || !newStandId || !newStartTime}
                sx={{ textTransform: 'none', borderRadius: 2 }}
              >
                {editingSessionId ? 'Zapisz zmiany w sesji' : 'Dodaj pracę na maszynie'}
              </Button>

              {editingSessionId && (
                <Button
                  variant="outlined"
                  color="inherit"
                  startIcon={<CancelIcon />}
                  onClick={resetMachineForm}
                  sx={{ textTransform: 'none', borderRadius: 2 }}
                >
                  Anuluj edycję sesji
                </Button>
              )}
            </Box>
          </Box>

          <Divider sx={{ my: 2 }} />

          <TextField 
            label="Notatka"
            multiline
            rows={2}
            fullWidth
            sx={{ mb: 2 }}
          />

          <Button
            variant="outlined"
            fullWidth
            sx={{
              color: attendance?.check_out === null ? 'error.main' : 'success.main',
              borderColor: attendance?.check_out === null ? 'error.main' : 'success.main'
            }}
            onClick={attendance?.check_out === null ? endShift : resumeShift}
          >
            {attendance?.check_out === null ? 'Zakończ zmianę' : 'Wznów zmianę'}
          </Button>

          <Button
            variant="text"
            fullWidth
            color="error"
            sx={{ mt: 1 }}
            onClick={() => setDeleteConfirmOpen(true)}
          >
            Usuń wpis
          </Button>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={onClose}>Anuluj</Button>
          <Button variant="contained" onClick={handleSave}>Zapisz</Button>
        </DialogActions>

        <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
          <DialogTitle>Usunąć wpis?</DialogTitle>
          <DialogContent>
            Czy na pewno chcesz usunąć ten wpis obecności? Tej operacji nie można cofnąć.
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteConfirmOpen(false)}>Anuluj</Button>
            <Button color="error" variant="contained" onClick={handleDeleteConfirm}>Usuń</Button>
          </DialogActions>
        </Dialog>
      </Dialog>
  );
}

export default AttendanceEditModal;